/**
 * `slot.v1` — wire → canonical decoder for Slot-shaped cells.
 *
 * Used by `scheduling-link` rows. The generated `SheetsNetwork.SlotCell`
 * model hydrates DateTime fields and validates the object shape.
 *
 * Canonical shape (`dtype.Slot`):
 *   `{ id: SlotId, start: DateTime<Utc>, end: DateTime<Utc>, bookedBy: Option<UserId> }`
 *   (a `bookedBy` that is not a user id — an empty string — is `null`)
 *
 * Null return: missing `id` / `start` / `end`, or timestamps
 * not parseable as ISO datetimes.
 */

import type { CellValue, Slot } from "../core/dtype.ts";
import type { EncodingDecoder } from "../core/encoding-registry.ts";
import { Json } from "../core/json.ts";
import { SheetsNetwork } from "../wire/rows-network.ts";
import type { Option } from "../core/option.ts";
import { UserId } from "../ids/ids.ts";

/** The `slot.v1` wire-encoding name a scheduling-slot source row declares in its metadata. */
export const SLOT_V1 = "slot.v1";

/** Decodes a `slot.v1` wire cell to a canonical slot (`null`: not a slot). */
export const slotV1: EncodingDecoder = (wire): Option<Json | CellValue> => {
  const raw = Json.object(Json.wire(wire));
  if (raw == null) return null;
  const result = SheetsNetwork.SlotCell.safeHydrate(raw);
  if (!result.isOk) return null;
  const parsed = result.value;
  return {
    id: parsed.id,
    start: parsed.start,
    end: parsed.end,
    bookedBy: parsed.bookedBy != null ? UserId.parse(parsed.bookedBy) : null,
  } satisfies Slot;
};
